"use client";

import { useEffect, useRef, useState } from "react";
import {
  HERO_POSTER_SRCSET,
  HERO_POSTER_SIZES,
  HERO_POSTER_FALLBACK,
  HERO_POSTER_1280,
} from "@/lib/marketing/hero-poster";

// D-038 media Wave 1 follow-up (PASTE #155): Tesla-pattern hero video.
// Muted loop + subtle bottom-right mute / unmute button so users can opt
// in to the 30s loop audio.
//
// PASTE #215 (LCP): the mobile LCP was the multi-MB hero video. To make
// the 27KB Cayambe poster the settled LCP, the poster now renders as a
// real priority <img> (the stable, largest first paint), and the heavy
// <video> is mounted OVER it only after first paint (load + idle). Same-
// size later paints don't replace an already-recorded LCP, so the poster
// stays the LCP element. prefers-reduced-motion users never mount the
// video, so the still poster is the whole hero.
//
// Server-rendered usage from app/page.tsx -> HomeHero.

// PASTE #212: real Cayambe volcano still (the video's opening frame), AVIF.
// MKT-#3: the poster URLs (srcset + same-origin /cdn/) live in
// lib/marketing/hero-poster so the <img> and the app/page.tsx preload never
// diverge. HERO_POSTER_FALLBACK (1920w, same-origin) is the src fallback +
// the <video> poster attribute.

type Props = {
  ariaLabel: string;
  muteLabel: string;
  unmuteLabel: string;
};

export function HomeHeroVideo({ ariaLabel, muteLabel, unmuteLabel }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  // The video is not in the initial HTML; it mounts after first paint so
  // it can't become the LCP element ahead of the poster <img>.
  const [showVideo, setShowVideo] = useState(false);

  // Mount the video after the page has loaded + the main thread is idle,
  // so the preloaded poster settles as the LCP first. Reduced-motion
  // users never mount it (poster-only hero). (§2.4 + §2.5)
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let cancelled = false;
    const mount = (): void => {
      if (!cancelled) setShowVideo(true);
    };
    const schedule = (): void => {
      if (cancelled) return;
      // requestIdleCallback is missing on older Safari; fall back to a
      // short timeout. Typed optional so the else branch stays reachable.
      const ric = (
        window as Window & {
          requestIdleCallback?: (
            cb: () => void,
            opts?: { timeout: number },
          ) => number;
        }
      ).requestIdleCallback;
      if (typeof ric === "function") {
        ric(mount, { timeout: 2500 });
      } else {
        window.setTimeout(mount, 400);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
    };
  }, []);

  // Once the video is in the DOM, sync muted state + start playback.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = muted;
    if (showVideo) {
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [showVideo, muted]);

  const handleToggle = (): void => {
    setMuted((prev) => !prev);
  };

  return (
    <>
      {/* LCP element: the preloaded Cayambe poster as a real image.
          Intentionally a plain <img>, not next/image: full-bleed AVIF,
          already optimized and hand-preloaded (fetchPriority high) in
          app/page.tsx; next/image would re-proxy it and fight that LCP
          preload. MKT-#3: srcset/sizes match the responsive preload so the
          viewport-correct variant (1280w on phones) is the one used. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={HERO_POSTER_FALLBACK}
        srcSet={HERO_POSTER_SRCSET}
        sizes={HERO_POSTER_SIZES}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        data-testid="home-hero-poster"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {showVideo && (
        <video
          ref={videoRef}
          data-testid="home-hero-video"
          aria-label={ariaLabel}
          // MKT-#11 (LCP regression): the video's poster must NOT out-paint the
          // already-settled hero <img>. The poster attribute is a single URL
          // (no responsive srcset), so pointing it at the 1280w mobile variant
          // makes it the SAME resource the phone already fetched + painted for
          // the <img> - a cache hit, never a second, LARGER download (the 1920w
          // poster was the late 2nd LCP candidate after #10 freed the main
          // thread and let the video mount earlier).
          poster={HERO_POSTER_1280}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 h-full w-full object-cover"
          data-media-slot="home-hero-video"
        >
          {/* PASTE #223: quality-first re-encode WITH audio (the #220
              size-first pass over-compressed + stripped audio). Deferred
              (#215) so size isn't LCP-critical. AV1 -> HEVC(hvc1) -> H264;
              the mute/unmute toggle below (same UX as the sign-in panel)
              now has real audio to unmute. */}
          <source
            src="https://d154w98knb4f7r.cloudfront.net/marketing/cayambe-hero-loop-1080p-hqd.webm"
            type='video/webm; codecs="av01.0.05M.08"'
          />
          <source
            src="https://d154w98knb4f7r.cloudfront.net/marketing/cayambe-hero-loop-1080p-hqd.hevc.mp4"
            type='video/mp4; codecs="hvc1"'
          />
          <source
            src="https://d154w98knb4f7r.cloudfront.net/marketing/cayambe-hero-loop-1080p-hqd.mp4"
            type='video/mp4'
          />
          <track
            kind="descriptions"
            srcLang="en"
            src="/captions/hero.en.vtt"
            label="English"
            default
          />
          <track
            kind="descriptions"
            srcLang="es"
            src="/captions/hero.es.vtt"
            label="Español"
          />
        </video>
      )}
      {showVideo && (
        <button
          type="button"
          data-testid="home-hero-mute-toggle"
          aria-label={muted ? unmuteLabel : muteLabel}
          aria-pressed={!muted}
          onClick={handleToggle}
          className="group absolute bottom-6 right-6 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/80 backdrop-blur-sm transition-all hover:border-brand-teal/60 hover:bg-black/60 hover:text-brand-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/70 md:bottom-8 md:right-8"
        >
          {muted ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4"
              aria-hidden
            >
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4"
              aria-hidden
            >
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <path d="M15.54 8.46a5 5 0 010 7.07" />
              <path d="M19.07 4.93a10 10 0 010 14.14" />
            </svg>
          )}
        </button>
      )}
    </>
  );
}
